/** API contract shared by the Worker and the client. */
import type { MapDoc, MapKind } from './doc.ts';

export interface UserInfo {
  id: string;
  name: string;
  initials: string;
}

/** Server representation of a map (doc is null for tombstones). */
export interface MapRecord {
  id: string;
  kind: MapKind;
  title: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  rev: number;
  /** The account's change counter when this record last changed in any way (document, image or thumbnail). */
  seq: number;
  imageType: string;
  imageBytes: number;
  w: number;
  h: number;
  hasImage: boolean;
  hasThumb: boolean;
  imageRev: number;
  /** Bumped with every thumbnail stored: a device fetches a thumbnail again only when this moves. */
  thumbRev: number;
  doc: MapDoc | null;
}

export interface MapPut {
  id: string;
  kind: MapKind;
  title: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
  imageType: string;
  imageBytes: number;
  w: number;
  h: number;
  doc: MapDoc | null;
}

/**
 * Sync is one round trip: what this device changed, and in the same answer everything that
 * changed anywhere since its cursor. The cursor is the server's own change counter (`seq`) —
 * never a clock — so nothing can be missed, whatever time a device thinks it is.
 */
export interface SyncRequest {
  /** The last `cursor` this device received (0 on a fresh device). */
  since: number;
  puts: MapPut[];
  /** Ask for the index of every live map too: the once-a-day check that both sides hold the same things. */
  check?: boolean;
}
export type SyncAck =
  /** Stored as sent. `record` carries no doc (the device has it). */
  | { id: string; status: 'stored'; record: MapRecord }
  /** The server already held a newer version: `record` is that version, doc and all. */
  | { id: string; status: 'newer'; record: MapRecord }
  /** This one map was refused (a document that does not validate); the others are unaffected. */
  | { id: string; status: 'rejected'; error: string };
export interface SyncResult {
  cursor: number;
  /** More changes are waiting: ask again from `cursor`. */
  more: boolean;
  acks: SyncAck[];
  /** Records changed since `since`, other than the ones just stored by this request. */
  maps: MapRecord[];
  /** Every live map as [id, rev, thumbRev] — when asked for, or when the cursor could not be trusted. */
  index?: [string, number, number][];
  serverTime: number;
}
/** Answer to storing an image or a thumbnail. */
export interface BlobStored {
  seq: number;
  hasImage: boolean;
  hasThumb: boolean;
  imageRev: number;
  thumbRev: number;
}

/** The first protocol's listing (clients that have not yet updated themselves still ask for it). */
export interface SyncResponse {
  maps: MapRecord[];
  cursor: number;
  serverTime: number;
}

export interface SignInRequest {
  code: string;
  device: string;
}
export interface SignInResponse {
  token: string;
  user: UserInfo;
}
export interface MeResponse {
  user: UserInfo;
  usage: { bytes: number; maps: number };
  serverTime: number;
}
export interface AdminUser {
  id: string;
  name: string;
  initials: string;
  createdAt: number;
  disabledAt: number | null;
  hasCode: boolean;
}
export interface AdminUserDetail extends AdminUser {
  sessions: { device: string; createdAt: number; lastSeenAt: number }[];
  usage: { bytes: number; maps: number };
}
export interface CodeResponse {
  code: string;
}
export interface ApiError {
  error: string;
  code: string;
}

export const MAX_IMAGE_BYTES = 24 * 1024 * 1024;
export const MAX_THUMB_BYTES = 512 * 1024;
export const MAX_DOC_BYTES = 2 * 1024 * 1024;
/** One sync request: a few documents at most (the device sends the rest in the next one). */
export const MAX_SYNC_BYTES = 6 * 1024 * 1024;
export const TOKEN_PREFIX = 'mm1';
/** Personal sign-in codes: 12 characters from an unambiguous alphabet (60 bits). */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 12;
export const SESSION_TTL_MS = 365 * 24 * 3600 * 1000;

export function normalizeCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/O/g, '0').replace(/I/g, '1');
}
export function formatCode(code: string): string {
  return code.replace(/(.{4})(?=.)/g, '$1-');
}
